import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { reportsAPI } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import CallingAlertsBanner from '../ticketing/CallingAlertsBanner';

// 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md 3.2 + 1.6): the
// AI call-center dashboard — volume, answer rate, time to first reply vs the
// 1.5 s target, where a turn's time goes, transfers, failures. Backend:
// GET /advanced-reports/call-center/summary
// (app/services/calling/call_center_reports.py). Every chart is ONE series in
// the theme's --chart-1 (no legend needed; the title names it); labels use
// text tokens, not the series colour.

type Summary = any;

const BAR = 'hsl(var(--chart-1))';
const AXIS = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };

const toDateString = (d: Date) => d.toISOString().split('T')[0];
const secs = (ms?: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`);
const pct = (v?: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);

function Tile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ChartCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
        {description && <CardDescription className="text-xs">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="h-56">{children}</CardContent>
    </Card>
  );
}

const tooltipStyle = {
  contentStyle: {
    background: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: 8,
    fontSize: 12,
    color: 'hsl(var(--popover-foreground))',
  },
  cursor: { fill: 'hsl(var(--muted))', opacity: 0.4 },
};

export default function CallCenterDashboard({ dateRange }: { dateRange: { startDate: Date; endDate: Date } }) {
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    reportsAPI
      .getCallCenterSummary({ start_date: toDateString(dateRange.startDate), end_date: toDateString(dateRange.endDate) })
      .then((d: Summary) => !cancelled && setData(d))
      .catch((e: any) => !cancelled && setError(e?.response?.data?.detail || e?.message || 'Failed to load'));
    return () => {
      cancelled = true;
    };
  }, [dateRange.startDate, dateRange.endDate]);

  if (error) return <p className="p-6 text-sm text-destructive">{String(error)}</p>;
  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading call center…
      </div>
    );
  }

  const fr = data.first_reply || {};
  const budget = data.latency_budget_ms || {};
  const budgetRows = [
    { step: 'Silence wait', ms: budget.silence_wait },
    { step: 'Speech-to-text', ms: budget.speech_to_text },
    { step: 'AI first word', ms: budget.ai_first_word },
    { step: 'First reply heard', ms: budget.first_reply_heard },
  ].filter((r) => r.ms != null);
  const hourRows = (data.per_hour || []).map((h: any) => ({ ...h, label: `${String(h.hour).padStart(2, '0')}:00` }));

  return (
    <div className="space-y-4">
      <CallingAlertsBanner />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label="Calls" value={data.calls} hint={`${data.connected} answered (${pct(data.answer_rate)})`} />
        <Tile
          label="Time to first reply"
          value={secs(fr.p50_ms)}
          hint={`median · 90% within ${secs(fr.p90_ms)}`}
        />
        <Tile
          label={`Replies within ${secs(fr.target_ms)}`}
          value={pct(fr.within_target)}
          hint={`${fr.turns_measured || 0} answers measured`}
        />
        <Tile label="Average call" value={secs(data.avg_duration_ms)} hint={`${data.avg_turns ?? '—'} turns`} />
        <Tile label="Transferred to a person" value={data.transfers} hint={`${data.transfers_completed} connected`} />
        <Tile label="Busy (over call limit)" value={data.overflow} />
        <Tile label="Failed to connect" value={data.failed} />
        <Tile label="Hung up without speaking" value={data.silent_hangups} hint={`${data.barge_ins} interruptions`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Calls per day">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.per_day} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.5} />
              <XAxis dataKey="date" tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(d: string) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
              <Tooltip {...tooltipStyle} formatter={(v: any) => [v, 'Calls']} />
              <Bar dataKey="calls" fill={BAR} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Calls by hour of day" description="Server time (UTC)">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourRows} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.5} />
              <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} interval={2} />
              <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
              <Tooltip {...tooltipStyle} formatter={(v: any) => [v, 'Calls']} />
              <Bar dataKey="calls" fill={BAR} radius={[4, 4, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title="Where a turn's time goes"
          description="Average per answered turn, from the caller going quiet. Target: first reply within 1.5s."
        >
          {budgetRows.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={budgetRows} layout="vertical" margin={{ top: 4, right: 24, left: 24, bottom: 0 }}>
                <CartesianGrid horizontal={false} stroke="hsl(var(--border))" strokeOpacity={0.5} />
                <XAxis type="number" tick={AXIS} tickLine={false} axisLine={false} tickFormatter={(v: number) => `${(v / 1000).toFixed(1)}s`} />
                <YAxis type="category" dataKey="step" tick={AXIS} tickLine={false} axisLine={false} width={110} />
                <Tooltip {...tooltipStyle} formatter={(v: any) => [secs(v), 'Average']} />
                <Bar dataKey="ms" fill={BAR} radius={[0, 4, 4, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground">No answered turns in this period.</p>
          )}
        </ChartCard>

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Providers that actually ran</CardTitle>
            <CardDescription className="text-xs">Per answered turn. A backup provider means the main one failed.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {[
              ['Speech-to-text', data.providers?.speech_to_text],
              ['Voice', data.providers?.voice],
            ].map(([label, counts]: any) => (
              <div key={label}>
                <p className="text-xs text-muted-foreground">{label}</p>
                {counts && Object.keys(counts).length ? (
                  <ul className="mt-1 space-y-0.5">
                    {Object.entries(counts).sort((a: any, b: any) => b[1] - a[1]).map(([name, n]: any) => (
                      <li key={name} className="flex justify-between tabular-nums">
                        <span className="font-mono text-xs">{name}</span>
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">—</p>
                )}
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              Speech-to-text fell back to local whisper on {pct(data.providers?.speech_to_text_fallback_rate)} of turns.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
